import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { CheckCircle2, Circle, ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Post-Match Wizard — UI orchestration only.
 * Uses existing endpoints:
 *   POST /api/matches                 (match report creation)
 *   GET  /api/matches/{id}/performances (to check which athletes already have a report)
 *   POST /api/matches/{id}/performance  (per-athlete evaluation)
 *
 * Flow:
 *   Step 1 — Create / select match   (if open && no matchId, creates it)
 *   Step 2 — Evaluate athletes       (loop, skip already-evaluated)
 *   Step 3 — Summary + reminder to prompt players for reflection
 */
const PostMatchWizard = ({ open, onOpenChange, athletes = [], onCompleted }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Step 1 — match report
  const [match, setMatch] = useState({
    date: '',
    opponent: '',
    competition: '',
    location: '',
    result: '',
  });
  const [createdMatch, setCreatedMatch] = useState(null);

  // Step 2 — per-athlete evaluation
  const [squadFilter, setSquadFilter] = useState('all');
  const [evaluated, setEvaluated] = useState(new Set()); // athlete_ids already reported
  const [currentAthleteId, setCurrentAthleteId] = useState(null);
  const [perf, setPerf] = useState({
    minutes_played: '',
    position_played: '',
    rating: '',
    coach_comment: '',
    strengths: '',
    improvements: '',
  });

  const squads = useMemo(() => {
    const s = new Set();
    athletes.forEach((a) => {
      if (a?.squad) s.add(a.squad);
    });
    return ['all', ...Array.from(s).sort()];
  }, [athletes]);

  const visibleAthletes = useMemo(
    () => (squadFilter === 'all' ? athletes : athletes.filter((a) => a.squad === squadFilter)),
    [squadFilter, athletes]
  );

  // Reset when opening
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setCreatedMatch(null);
    setMatch({ date: '', opponent: '', competition: '', location: '', result: '' });
    setEvaluated(new Set());
    setCurrentAthleteId(null);
    setPerf({ minutes_played: '', position_played: '', rating: '', coach_comment: '', strengths: '', improvements: '' });
  }, [open]);

  const h = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  const createMatch = async () => {
    if (!match.date || !match.opponent || !match.competition || !match.location) {
      toast.error('Please fill date, opponent, competition and location.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/matches`,
        { ...match, target_type: 'all', target_squads: [], target_athletes: [] },
        h
      );
      setCreatedMatch(res.data);
      // Fetch existing performances (in case re-opened for same match later)
      try {
        const perfs = (await axios.get(`${API}/api/matches/${res.data.id}/performance`, h)).data || [];
        setEvaluated(new Set(perfs.map((p) => p.athlete_id)));
      } catch {
        /* non-fatal */
      }
      toast.success('Match created — evaluate athletes next.');
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create match');
    } finally {
      setSubmitting(false);
    }
  };

  const openEvalFor = (athleteId) => {
    setCurrentAthleteId(athleteId);
    setPerf({ minutes_played: '', position_played: '', rating: '', coach_comment: '', strengths: '', improvements: '' });
  };

  const submitEval = async () => {
    if (!createdMatch || !currentAthleteId) return;
    setSubmitting(true);
    try {
      const payload = {
        athlete_id: currentAthleteId,
        minutes_played: perf.minutes_played ? parseInt(perf.minutes_played, 10) : null,
        position_played: perf.position_played || null,
        rating: perf.rating ? parseFloat(perf.rating) : null,
        coach_comment: perf.coach_comment || null,
        strengths: perf.strengths || null,
        improvements: perf.improvements || null,
      };
      await axios.post(`${API}/api/matches/${createdMatch.id}/performance`, payload, h);
      setEvaluated((prev) => new Set(prev).add(currentAthleteId));
      toast.success('Evaluation saved.');
      setCurrentAthleteId(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save evaluation');
    } finally {
      setSubmitting(false);
    }
  };

  const finish = () => {
    onOpenChange(false);
    if (onCompleted) onCompleted(createdMatch);
  };

  const evaluatedCount = evaluated.size;
  const pendingCount = visibleAthletes.length - visibleAthletes.filter((a) => evaluated.has(a.id)).length;

  const StepHeader = () => (
    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400" data-testid="wizard-step-header">
      {[
        { n: 1, label: 'Match Report' },
        { n: 2, label: 'Evaluations' },
        { n: 3, label: 'Summary' },
      ].map(({ n, label }) => (
        <div key={n} className="flex items-center gap-2">
          <span
            className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold ${
              step === n
                ? 'bg-emerald-600 text-white'
                : step > n
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
            }`}
          >
            {step > n ? <CheckCircle2 size={10} /> : n}
          </span>
          <span className={step === n ? 'font-medium text-gray-900 dark:text-gray-100' : ''}>
            {label}
          </span>
          {n < 3 ? <span className="text-gray-300">›</span> : null}
        </div>
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="post-match-wizard">
        <DialogHeader>
          <DialogTitle>Post-Match Workflow</DialogTitle>
          <DialogDescription className="sr-only">
            Three-step post-match flow: capture the match report, evaluate each athlete, then review the summary.
          </DialogDescription>
          <StepHeader />
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {step === 1 && (
            <div className="space-y-4 py-2" data-testid="wizard-step-1">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Capture the match basics. You&apos;ll evaluate athletes in the next step.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={match.date} onChange={(e) => setMatch({ ...match, date: e.target.value })} data-testid="wizard-match-date" />
                </div>
                <div>
                  <Label>Opponent</Label>
                  <Input value={match.opponent} onChange={(e) => setMatch({ ...match, opponent: e.target.value })} placeholder="e.g., FC Porto" data-testid="wizard-match-opponent" />
                </div>
                <div>
                  <Label>Competition</Label>
                  <Input value={match.competition} onChange={(e) => setMatch({ ...match, competition: e.target.value })} placeholder="e.g., League" data-testid="wizard-match-competition" />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input value={match.location} onChange={(e) => setMatch({ ...match, location: e.target.value })} placeholder="Home / Away / Stadium" data-testid="wizard-match-location" />
                </div>
                <div className="col-span-2">
                  <Label>Result (optional)</Label>
                  <Input value={match.result} onChange={(e) => setMatch({ ...match, result: e.target.value })} placeholder="e.g., 3-1 Win" data-testid="wizard-match-result" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3 py-2" data-testid="wizard-step-2">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium text-gray-900 dark:text-gray-100" data-testid="wizard-eval-progress">
                    {evaluatedCount}
                  </span>{' '}
                  evaluated · {pendingCount} pending
                </div>
                <select
                  className="text-xs border rounded-md px-2 py-1 bg-white dark:bg-gray-800 dark:border-gray-700"
                  value={squadFilter}
                  onChange={(e) => setSquadFilter(e.target.value)}
                  data-testid="wizard-squad-filter"
                >
                  {squads.map((s) => (
                    <option key={s} value={s}>
                      {s === 'all' ? 'All squads' : s}
                    </option>
                  ))}
                </select>
              </div>
              {currentAthleteId ? (
                <EvalForm
                  athlete={visibleAthletes.find((a) => a.id === currentAthleteId)}
                  perf={perf}
                  setPerf={setPerf}
                  onCancel={() => setCurrentAthleteId(null)}
                  onSubmit={submitEval}
                  submitting={submitting}
                />
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-800 border rounded-md dark:border-gray-800 max-h-[50vh] overflow-y-auto">
                  {visibleAthletes.length === 0 ? (
                    <li className="py-6 text-center text-xs text-gray-400">No athletes match this filter.</li>
                  ) : (
                    visibleAthletes.map((a) => {
                      const done = evaluated.has(a.id);
                      return (
                        <li
                          key={a.id}
                          className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                          data-testid={`wizard-athlete-row-${a.id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {done ? (
                              <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />
                            ) : (
                              <Circle size={14} className="text-gray-300 flex-shrink-0" />
                            )}
                            <span className="text-sm truncate">{a.full_name}</span>
                            {a.squad ? (
                              <Badge className="text-[9px] bg-gray-100 dark:bg-gray-700 text-gray-500">{a.squad}</Badge>
                            ) : null}
                          </div>
                          <Button
                            size="sm"
                            variant={done ? 'ghost' : 'default'}
                            className={`h-7 text-[11px] ${done ? '' : 'bg-pfap-green hover:bg-pfap-green/90 text-white'}`}
                            onClick={() => openEvalFor(a.id)}
                            data-testid={`wizard-open-eval-${a.id}`}
                          >
                            {done ? 'Edit' : 'Evaluate'}
                          </Button>
                        </li>
                      );
                    })
                  )}
                </ul>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 py-4 text-center" data-testid="wizard-step-3">
              <CheckCircle2 size={36} className="text-emerald-500 mx-auto" />
              <div>
                <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  Match captured: {createdMatch?.opponent}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {evaluatedCount} athlete{evaluatedCount === 1 ? '' : 's'} evaluated.
                </p>
              </div>
              <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/40 p-3 text-left">
                <p className="text-xs font-medium text-blue-900 dark:text-blue-200 mb-1">Next step — player reflections</p>
                <p className="text-[11px] text-blue-800 dark:text-blue-300">
                  Players will see their rating and coach notes on their dashboard. Remind them to log a brief reflection next session.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between mt-2 border-t pt-3 dark:border-gray-800">
          <Button
            variant="ghost"
            onClick={() => (step > 1 ? setStep(step - 1) : onOpenChange(false))}
            disabled={submitting}
            data-testid="wizard-back"
          >
            <ChevronLeft size={14} /> {step === 1 ? 'Cancel' : 'Back'}
          </Button>
          {step === 1 ? (
            <Button onClick={createMatch} disabled={submitting} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="wizard-next-step2">
              Next: Evaluate {submitting ? '…' : <ChevronRight size={14} />}
            </Button>
          ) : step === 2 ? (
            <Button onClick={() => setStep(3)} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="wizard-next-step3">
              Finish evaluations <ChevronRight size={14} />
            </Button>
          ) : (
            <Button onClick={finish} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="wizard-done">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EvalForm = ({ athlete, perf, setPerf, onCancel, onSubmit, submitting }) => (
  <div className="rounded-md border dark:border-gray-800 p-3 space-y-3" data-testid="wizard-eval-form">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{athlete?.full_name || ''}</p>
        <p className="text-[11px] text-gray-400">
          {athlete?.squad || 'Unassigned'} {athlete?.position ? ` · ${athlete.position}` : ''}
        </p>
      </div>
      <Button variant="ghost" size="sm" onClick={onCancel} className="text-xs">Back</Button>
    </div>
    <div className="grid grid-cols-3 gap-2">
      <div>
        <Label className="text-[11px]">Minutes</Label>
        <Input type="number" min="0" max="120" value={perf.minutes_played}
          onChange={(e) => setPerf({ ...perf, minutes_played: e.target.value })}
          data-testid="wizard-eval-minutes" />
      </div>
      <div>
        <Label className="text-[11px]">Position</Label>
        <Input value={perf.position_played}
          onChange={(e) => setPerf({ ...perf, position_played: e.target.value })}
          placeholder="e.g., CM" data-testid="wizard-eval-position" />
      </div>
      <div>
        <Label className="text-[11px]">Rating (1-10)</Label>
        <Input type="number" min="1" max="10" step="0.5" value={perf.rating}
          onChange={(e) => setPerf({ ...perf, rating: e.target.value })}
          data-testid="wizard-eval-rating" />
      </div>
    </div>
    <div>
      <Label className="text-[11px]">Coach comment</Label>
      <Textarea rows={2} value={perf.coach_comment}
        onChange={(e) => setPerf({ ...perf, coach_comment: e.target.value })}
        placeholder="Overall note…" data-testid="wizard-eval-comment" />
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label className="text-[11px]">Strengths</Label>
        <Textarea rows={2} value={perf.strengths}
          onChange={(e) => setPerf({ ...perf, strengths: e.target.value })}
          placeholder="What worked…" data-testid="wizard-eval-strengths" />
      </div>
      <div>
        <Label className="text-[11px]">Improvements</Label>
        <Textarea rows={2} value={perf.improvements}
          onChange={(e) => setPerf({ ...perf, improvements: e.target.value })}
          placeholder="Focus area…" data-testid="wizard-eval-improvements" />
      </div>
    </div>
    <div className="flex justify-end">
      <Button onClick={onSubmit} disabled={submitting} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="wizard-eval-submit">
        {submitting ? 'Saving…' : 'Save evaluation'}
      </Button>
    </div>
  </div>
);

export default PostMatchWizard;
